import {
  Avatar,
  Button,
  IconButton,
  ModalDialog,
  Tag,
  Tooltip,
  useAlert,
} from '@ads/components-react';
import {
  BookOpenIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  KeyIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamCorrectExamAnsweridQueryOptions,
  useGetApiV1ExamExamItemSubTypes,
} from '@/shared/api/generated';
import {
  isTeachingNotesSubtype,
  MULTILINE_TEXT_COMPONENT_TYPE,
  parseKeyTerms,
} from '@/shared/lib/examItemSubtype';
import { ProgressBar, Skeleton } from '@/shared/ui/atoms';
import { useAiBatchSuggestions } from '../../../hooks/useAiBatchSuggestions';
import { useAnimatedBatchProgress } from '../../../hooks/useAnimatedBatchProgress';
import { useBatchAiCorrection } from '../../../hooks/useBatchAiCorrection';
import { usePendingAnswerIds } from '../../../hooks/usePendingAnswerIds';
import { useQuestionComponentType } from '../../../hooks/useQuestionComponentType';

export interface QuestionAiBatchActionProps {
  /** ID da publicação do exame */
  readonly examPublishId: number;
  /** ID do item de questão a corrigir em lote */
  readonly examItemId: number;
  /** Enunciado da questão (pode conter HTML), exibido no modal de confirmação */
  readonly questionLabel: string | null | undefined;
  /** Quantidade de respostas pendentes da questão (`correctionPendingCount`) */
  readonly pendingCount: number;
  /** Chamado após uma correção em lote bem-sucedida, para abrir o accordion da questão */
  readonly onBatchSuccess?: () => void;
}

/** Metadados de exibição da questão, vindos de uma resposta qualquer da fila. */
interface QuestionMeta {
  readonly componentType: number | null | undefined;
  readonly subtype: number | null | undefined;
  readonly author: string | null | undefined;
  readonly modelAnswer: string | null | undefined;
  readonly keyTerms: string | null | undefined;
}

/**
 * Molecule QuestionAiBatchAction — gatilho de correção em lote por IA de uma questão.
 *
 * Renderizado como irmão do `AccordionTrigger` da questão (nunca aninhado dentro
 * dele) para não colocar um `<button>` dentro de outro. Ao abrir a confirmação,
 * coleta os `answerId` pendentes da questão e busca tipo, autor, resposta-modelo e
 * termos-chave a partir de uma dessas respostas (o endpoint de detalhe é por
 * resposta, mas esses campos são os mesmos para toda a questão). A correção em
 * lote só é permitida para questões dissertativas (`MultilineText`) — o mesmo
 * critério do painel de IA individual no `CorrectionModal`. O próprio gatilho já
 * busca esse tipo antecipadamente (`useQuestionComponentType`, com uma amostra
 * qualquer da questão) e fica desabilitado quando ela não é suportada, sem
 * esperar o operador clicar para descobrir; o diálogo repete a checagem com o
 * tipo obtido de um pendente específico e explica o motivo caso a verificação
 * antecipada ainda não tenha resolvido a tempo do clique. Ao confirmar, envia os
 * `answerId` já coletados ao endpoint de correção em lote; o resultado fica
 * disponível como sugestão pré-carregada no `CorrectionModal` de cada aluno, sem
 * persistir nada automaticamente.
 *
 * A confirmação usa um `Dialog` (Radix) montado à mão em vez do `ModalDialog` do
 * ADS — esse componente é para confirmações simples, sem muito corpo, e aqui a
 * tela mostra enunciado, tipo, autor e referência de correção.
 *
 * Fechar (X, "Cancelar" ou Esc/clique fora) nunca fica bloqueado, mas se houver
 * uma correção em lote em andamento (`isCorrecting`), um segundo diálogo avisa
 * que todo o progresso — inclusive pedaços já concluídos — será perdido antes de
 * confirmar; ver `useBatchAiCorrection().cancel`.
 *
 * @example
 * ```tsx
 * <QuestionAiBatchAction
 *   examPublishId={123}
 *   examItemId={456}
 *   questionLabel={item.label}
 *   pendingCount={8}
 * />
 * ```
 */
export function QuestionAiBatchAction({
  examPublishId,
  examItemId,
  questionLabel,
  pendingCount,
  onBatchSuccess,
}: QuestionAiBatchActionProps) {
  const { t } = useTranslation('examCorrection');
  const { alert } = useAlert();
  const queryClient = useQueryClient();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [pendingAnswerIds, setPendingAnswerIds] = useState<number[] | null>(null);
  const [meta, setMeta] = useState<QuestionMeta | null>(null);
  // Confirmação extra antes de fechar durante uma correção em andamento — sem ela,
  // fechar perderia silenciosamente os pedaços já corrigidos.
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);

  const { collectPendingAnswerIds } = usePendingAnswerIds();
  const { setMany } = useAiBatchSuggestions();
  const {
    mutate: correctBatch,
    isPending: isCorrecting,
    progress,
    cancel: cancelBatch,
  } = useBatchAiCorrection();
  const animatedProgressPercentage = useAnimatedBatchProgress(progress);
  const subtypesQuery = useGetApiV1ExamExamItemSubTypes();
  // Verificação antecipada do tipo da questão — mantém o próprio gatilho
  // desabilitado para questões não suportadas, em vez de deixar o operador abrir
  // o diálogo só para descobrir isso. Só busca quando há pendentes (sem
  // pendentes o gatilho já fica desabilitado por `pendingCount`).
  const { componentType: earlyComponentType } = useQuestionComponentType(
    examPublishId,
    examItemId,
    pendingCount > 0,
  );
  const isKnownUnsupportedType =
    earlyComponentType !== undefined && earlyComponentType !== MULTILINE_TEXT_COMPONENT_TYPE;

  const isCollecting = pendingAnswerIds === null;
  const isBusy = isConfirmOpen && (isCollecting || isCorrecting);
  const subtypeLabel = subtypesQuery.data?.find(o => o.key === meta?.subtype)?.value;
  const keyTerms = parseKeyTerms(meta?.keyTerms);
  const modelAnswerLabel = isTeachingNotesSubtype(meta?.subtype)
    ? t('modal.reference.teachingNotes')
    : t('modal.reference.modelAnswer');

  // Pode ter mudado desde o clique no botão (outra pessoa corrigiu no meio do
  // caminho) — sem pendentes, não há o que enviar à IA.
  const hasNoneLeft = pendingAnswerIds !== null && pendingAnswerIds.length === 0;
  // A correção em lote usa o mesmo critério do painel de IA individual: só
  // questões dissertativas (MultilineText) são suportadas.
  const isUnsupportedType =
    !isLoadingMeta && meta !== null && meta.componentType !== MULTILINE_TEXT_COMPONENT_TYPE;

  async function handleOpen() {
    setIsConfirmOpen(true);
    setPendingAnswerIds(null);
    setMeta(null);
    setIsLoadingMeta(true);

    const answerIds = await collectPendingAnswerIds(examPublishId, examItemId);
    setPendingAnswerIds(answerIds);

    const firstAnswerId = answerIds[0];
    if (firstAnswerId === undefined) {
      setIsLoadingMeta(false);
      return;
    }

    try {
      const details = await queryClient.fetchQuery(
        getApiV1ExamCorrectExamAnsweridQueryOptions(firstAnswerId),
      );
      setMeta({
        componentType: details?.componentType,
        subtype: details?.subtype,
        author: details?.author,
        modelAnswer: details?.modelAnswer,
        keyTerms: details?.keyTerms,
      });
    } catch {
      // Metadados são só informativos no modal — uma falha aqui não deve impedir
      // a correção em lote em si (o tipo passa a ser tratado como suportado, já
      // que não há como confirmar a restrição).
      setMeta(null);
    } finally {
      setIsLoadingMeta(false);
    }
  }

  function handleClose() {
    // Coletar os pendentes é rápido e não tem nada de valor a perder — só a
    // correção em lote em andamento (isCorrecting) precisa do aviso.
    if (isCorrecting) {
      setIsCloseWarningOpen(true);
      return;
    }
    setIsConfirmOpen(false);
  }

  function handleCancelCloseWarning() {
    setIsCloseWarningOpen(false);
  }

  function handleConfirmCloseWarning() {
    cancelBatch();
    setIsCloseWarningOpen(false);
    setIsConfirmOpen(false);
  }

  function handleConfirm() {
    if (!pendingAnswerIds || pendingAnswerIds.length === 0) return;

    console.warn(pendingAnswerIds);

    correctBatch(
      { data: pendingAnswerIds },
      {
        onSuccess: results => {
          setMany(results);
          setIsConfirmOpen(false);
          onBatchSuccess?.();
          alert({
            alertType: 'success',
            title: t('correction.table.aiBatch.toast.successTitle'),
            description: t('correction.table.aiBatch.toast.successMessage', {
              corrected: results.length,
              total: pendingAnswerIds.length,
            }),
          });
        },
        onError: () => {
          setIsConfirmOpen(false);
          alert({
            alertType: 'warning',
            title: t('correction.table.aiBatch.toast.errorTitle'),
            description: t('correction.table.aiBatch.toast.errorMessage'),
          });
        },
      },
    );
  }

  return (
    <>
      <Tooltip title={t('correction.table.aiBatch.trigger')}>
        <IconButton
          hierarchy="secondary"
          size="md"
          icon={SparklesIcon}
          disabled={pendingCount === 0 || isKnownUnsupportedType}
          onClick={handleOpen}
          ariaLabel={t('correction.table.aiBatch.trigger')}
        />
      </Tooltip>

      <Dialog.Root open={isConfirmOpen} onOpenChange={next => !next && handleClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[700px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
            aria-describedby={undefined}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-lg py-mini">
              <div className="flex items-center gap-nano">
                <Dialog.Title className="flex items-center gap-nano text-ctx-content-title ts-heading-xs">
                  <SparklesIcon className="size-xxs text-ctx-highlight-promote" />
                  {t('correction.table.aiBatch.confirm.title')}
                </Dialog.Title>
                <Tooltip
                  title={t('correction.table.aiBatch.confirm.description', {
                    count: pendingCount,
                  })}
                  custom={{ root: 'z-[60] max-w-[18rem]' }}
                >
                  <InformationCircleIcon className="size-xxxs shrink-0 text-ctx-content-placeholder" />
                </Tooltip>
              </div>
              <Dialog.Close asChild>
                <IconButton
                  icon={XMarkIcon}
                  hierarchy="tertiary"
                  size="sm"
                  ariaLabel={t('correction.table.aiBatch.confirm.close')}
                />
              </Dialog.Close>
            </div>

            {/* Body */}
            <div className="at-scrollbar flex-1 overflow-y-auto px-lg py-xxs">
              {hasNoneLeft ? (
                <p className="text-ctx-content-base ts-paragraph-xxs">
                  {t('correction.table.aiBatch.confirm.noneLeft')}
                </p>
              ) : (
                <div className="flex flex-col gap-xxs">
                  {isLoadingMeta ? (
                    <Skeleton className="h-sm w-[5rem] rounded-pill" />
                  ) : (
                    subtypeLabel && (
                      <div className="flex">
                        <Tag
                          label={subtypeLabel}
                          size="sm"
                          status="highlight"
                          custom={{ text: 'text-nowrap' }}
                        />
                      </div>
                    )
                  )}

                  {questionLabel && (
                    <p
                      dangerouslySetInnerHTML={{ __html: questionLabel }}
                      className="font-bold text-ctx-content-title ts-paragraph-xs"
                    />
                  )}

                  <div className="flex flex-col gap-nano">
                    <span className="text-ctx-content-placeholder ts-caption-xxs">
                      {t('modal.fields.author')}
                    </span>
                    {isLoadingMeta ? (
                      <Skeleton className="h-mini w-[8rem]" />
                    ) : (
                      <div className="flex items-center gap-nano">
                        <Avatar
                          size="md"
                          fallback={(meta?.author ?? 'A').charAt(0).toUpperCase()}
                        />
                        <span className="text-ctx-content-title ts-subtitle-xxs">
                          {meta?.author ?? t('modal.unknownAuthor')}
                        </span>
                      </div>
                    )}
                  </div>

                  {isUnsupportedType && (
                    <div className="flex gap-nano rounded-sm border-hairline border-base-warning-300 bg-base-warning-100 p-micro text-base-warning-800 ts-paragraph-xxs">
                      <ExclamationTriangleIcon className="mt-quark size-xxs shrink-0" />
                      <span>{t('correction.table.aiBatch.confirm.unsupportedType')}</span>
                    </div>
                  )}

                  {!isLoadingMeta &&
                    !isUnsupportedType &&
                    (meta?.modelAnswer || keyTerms.length > 0) && (
                      <div className="flex flex-col gap-nano">
                        {meta?.modelAnswer && (
                          <div className="flex flex-col gap-quark">
                            <span className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
                              <BookOpenIcon className="size-xxxs" />
                              {modelAnswerLabel}
                            </span>
                            <div className="rounded-sm border-thin border-ctx-layout-border bg-ctx-layout-body p-xs">
                              <p className="whitespace-pre-wrap text-ctx-content-base ts-paragraph-xxs">
                                {meta.modelAnswer}
                              </p>
                            </div>
                          </div>
                        )}

                        {keyTerms.length > 0 && (
                          <div className="flex flex-col gap-quark">
                            <span className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
                              <KeyIcon className="size-xxxs" />
                              {t('modal.reference.keyTerms')}
                            </span>
                            <ul
                              className="flex flex-wrap gap-quark"
                              aria-label={t('modal.reference.keyTerms')}
                            >
                              {keyTerms.map(term => (
                                <li key={term}>
                                  <Tag
                                    label={term}
                                    size="sm"
                                    status="default"
                                    custom={{ text: 'text-nowrap' }}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Progresso do envio em pedaços — substitui o spinner indefinido do botão por uma barra com percentual real */}
            {isCorrecting && progress && (
              <div className="flex flex-col gap-quark border-t-hairline border-ctx-layout-border px-lg py-xxs">
                <span className="text-ctx-content-placeholder ts-caption-xxs">
                  {t('correction.table.aiBatch.confirm.progress')}
                </span>
                <ProgressBar
                  progressPercentage={Math.round(animatedProgressPercentage)}
                  shouldShowPercentage
                />
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-end gap-micro border-t-hairline border-ctx-layout-border px-lg py-xxs">
              <Button hierarchy="secondary" size="md" onClick={handleClose}>
                {t('correction.table.aiBatch.confirm.cancel')}
              </Button>
              <Button
                hierarchy="primary"
                size="md"
                onClick={handleConfirm}
                isLoading={isBusy}
                disabled={isBusy || hasNoneLeft || isUnsupportedType}
              >
                {t('correction.table.aiBatch.confirm.confirm')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Aviso aninhado — só aparece por cima do diálogo principal quando o operador
          tenta fechar (X, "Cancelar" ou Esc/clique fora) com uma correção em lote em
          andamento. Confirmar aqui desiste da correção via `cancelBatch`, descartando
          até os pedaços já concluídos — coerente com o aviso de que tudo se perde. */}
      <ModalDialog
        open={isCloseWarningOpen}
        onOpenChange={next => !next && handleCancelCloseWarning()}
        title={t('correction.table.aiBatch.closeWarning.title')}
        type="danger"
        description={t('correction.table.aiBatch.closeWarning.body')}
        primaryAction={{
          label: t('correction.table.aiBatch.closeWarning.confirm'),
          onClick: handleConfirmCloseWarning,
        }}
        secondaryAction={{
          label: t('correction.table.aiBatch.closeWarning.cancel'),
          onClick: handleCancelCloseWarning,
        }}
      />
    </>
  );
}
